import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Calendar, MapPin, Clock, Download, UserCheck, XCircle,
  CheckCircle, Plus, Crown, Gift, ClipboardList, QrCode, ScanLine,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import EventPhotoUploader from '@/components/EventPhotoUploader';
import api from '@/lib/api';

// Registrations + attendance + per-event financial capture (Phase 3)
function EventQrDialog({ open, onOpenChange, eventId, eventTitle }) {
  const canvasWrapRef = useRef(null);
  const eventUrl = `${window.location.origin}/events/${eventId}`;

  const downloadPng = () => {
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `event_qr_${(eventTitle || 'event').replace(/[^a-z0-9]+/gi, '_').slice(0, 30)}.png`;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Event QR Code</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2" data-testid="event-qr-dialog">
          <div ref={canvasWrapRef} className="bg-white p-4 rounded-2xl border border-border-subtle">
            <QRCodeCanvas value={eventUrl} size={220} fgColor="#0d3b66" level="M" includeMargin />
          </div>
          <p className="text-xs text-text-mid text-center break-all">{eventUrl}</p>
          <p className="text-xs text-text-mid text-center">
            Print this for flyers or the door — scanning opens the event page where moms can RSVP or find their check-in code.
          </p>
          <div className="flex gap-2 w-full">
            <Button onClick={downloadPng} className="flex-1 bg-teal hover:bg-teal-deep rounded-full" data-testid="download-event-qr-btn">
              <Download size={15} className="mr-2" /> Download PNG
            </Button>
            <Button variant="outline" onClick={() => window.print()} className="flex-1 rounded-full" data-testid="print-event-qr-btn">
              Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function EventOpsPanel({ eventId, isWorkshop }) {
  const [reg, setReg] = useState(null);
  const [fin, setFin] = useState(null);
  const [marking, setMarking] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${eventId}/registrations`);
      setReg(r.data);
    } catch { /* silent */ }
    if (isWorkshop) {
      try {
        const f = await api.get(`/workshops/${eventId}/financials`);
        setFin(f.data);
      } catch { /* silent */ }
    }
  }, [eventId, isWorkshop]);

  useEffect(() => { load(); }, [load]);

  const markAttendance = async (rsvpId, attended) => {
    setMarking(rsvpId);
    try {
      await api.post(`/events/${eventId}/attendance`, { rsvp_id: rsvpId, attended });
      setReg((prev) => ({
        ...prev,
        registrations: prev.registrations.map((r) => (r.rsvp_id === rsvpId ? { ...r, attended } : r)),
      }));
      if (isWorkshop) {
        try { const f = await api.get(`/workshops/${eventId}/financials`); setFin(f.data); } catch { /* */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update attendance');
    }
    setMarking(null);
  };

  const exportAttendees = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiUrl}/api/events/${eventId}/attendees/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendees_${eventId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Attendee CSV exported');
    } catch {
      toast.error('Export failed');
    }
  };

  if (!reg) return null;
  const bd = reg.tier_breakdown || {};

  return (
    <>
      {isWorkshop && fin && (
        <div className="bg-white rounded-2xl shadow-card p-6 mb-6" data-testid="event-financials-card">
          <h3 className="font-semibold text-navy flex items-center gap-2 mb-4">
            <Crown size={18} className="text-navy" /> Financial Capture
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-center">
            <div className="bg-cream/60 rounded-xl p-3">
              <p className="text-lg font-bold text-navy">${Number(fin.base_price).toFixed(2)}</p>
              <p className="text-[11px] text-text-mid">Base price</p>
            </div>
            <div className="bg-cream/60 rounded-xl p-3">
              <p className="text-lg font-bold text-navy">{fin.tickets_full_price.count}</p>
              <p className="text-[11px] text-text-mid">Full price (${(fin.tickets_full_price.gross_cents / 100).toFixed(2)})</p>
            </div>
            <div className="bg-cream/60 rounded-xl p-3">
              <p className="text-lg font-bold text-navy">{fin.tickets_member_30off.count}</p>
              <p className="text-[11px] text-text-mid">30% member (${(fin.tickets_member_30off.gross_cents / 100).toFixed(2)})</p>
            </div>
            <div className="bg-cream/60 rounded-xl p-3">
              <p className="text-lg font-bold text-teal">{fin.registrations_credit_comped}</p>
              <p className="text-[11px] text-text-mid">Credit comped</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3">
              <p className="text-lg font-bold text-amber-700" data-testid="fin-guest-passes">{fin.guest_pass_redemptions || 0}</p>
              <p className="text-[11px] text-text-mid">Guest passes ($0 rev)</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3">
              <p className="text-lg font-bold text-green-700">${(fin.total_gross_cents / 100).toFixed(2)}</p>
              <p className="text-[11px] text-text-mid">Total gross · {fin.total_attendance} attending</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-card p-6 mb-6" data-testid="event-registrations-section">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <h3 className="font-semibold text-navy flex items-center gap-2">
            <ClipboardList size={18} className="text-teal" />
            Registrations &amp; Attendance ({reg.registrations.length})
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="bg-teal/10 text-teal">Thrive: {bd.thrive || 0}</Badge>
            <Badge variant="outline">Connect: {bd.connect || 0}</Badge>
            <Badge variant="outline" className="bg-amber-50 text-amber-700">Guests: {bd.guest || 0}</Badge>
            <Button size="sm" variant="outline" onClick={exportAttendees}
              className="rounded-full border-teal text-teal hover:bg-teal hover:text-white"
              data-testid="export-attendees-btn">
              <Download size={14} className="mr-1" /> Export Attendees CSV
            </Button>
          </div>
        </div>
        {reg.registrations.length === 0 ? (
          <p className="text-center text-text-mid py-6">No registrations yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Attended</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reg.registrations.map((r) => (
                <TableRow key={r.rsvp_id} data-testid={`registration-row-${r.rsvp_id}`}>
                  <TableCell>
                    <div className="font-medium text-navy flex items-center gap-2">
                      {r.name}
                      {r.is_guest_pass && (
                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          <Gift size={10} /> GUEST{r.invited_by_name ? ` of ${r.invited_by_name}` : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-text-mid">{r.email}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={r.tier === 'thrive' ? 'bg-teal/10 text-teal' : r.tier === 'guest' ? 'bg-amber-50 text-amber-700' : ''}>
                      {r.tier}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{r.status}</TableCell>
                  <TableCell className="text-xs text-text-mid">
                    {r.pricing_label === 'workshop_free_credit' ? 'Monthly credit'
                      : r.pricing_label === 'workshop_member_30off' ? `30% off — $${((r.amount_cents || 0) / 100).toFixed(2)}`
                      : r.pricing_label === 'guest_pass_free' ? 'Guest pass — $0'
                      : r.amount_cents ? `$${(r.amount_cents / 100).toFixed(2)}`
                      : r.payment_method || '—'}
                  </TableCell>
                  <TableCell>
                    {r.status === 'confirmed' ? (
                      <input
                        type="checkbox"
                        checked={r.attended === true}
                        disabled={marking === r.rsvp_id}
                        onChange={(e) => markAttendance(r.rsvp_id, e.target.checked)}
                        className="w-4 h-4 accent-teal cursor-pointer"
                        data-testid={`attendance-checkbox-${r.rsvp_id}`}
                      />
                    ) : <span className="text-text-light text-xs">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </>
  );
}

const getEventStatusBadge = (status) => {
  const styles = {
    Upcoming: 'bg-green-100 text-green-700 border-green-300',
    Full: 'bg-orange-100 text-orange-700 border-orange-300',
    Past: 'bg-gray-100 text-gray-600 border-gray-300',
  };
  return styles[status] || 'bg-gray-100 text-gray-600';
};

export default function AdminEventsTab({
  selectedEvent, eventDetail, setSelectedEvent, setEventDetail,
  events, members, cities,
  addRsvpOpen, setAddRsvpOpen, addRsvpUserId, setAddRsvpUserId,
  setGuestModal,
  createEventOpen, setCreateEventOpen, newEvent, setNewEvent,
  setEditingEvent, setEditEventOpen,
  handleExportEventRsvps, handleDeleteEvent, handleAddRsvp, handleRemoveRsvp,
  handlePromoteFromWaitlist, handleRefundPayment, handleTransferCredit,
  handleCreateEvent, fetchEventDetail,
}) {
  const [qrOpen, setQrOpen] = useState(false);
  const navigate = useNavigate();
  if (selectedEvent && eventDetail) {
    return (
      <div data-testid="admin-events-detail">
        <button
          onClick={() => { setSelectedEvent(null); setEventDetail(null); }}
          className="flex items-center gap-2 text-teal hover:text-teal-deep mb-6"
        >
          <ArrowLeft size={18} /> Back to Events
        </button>

        {/* Event Info Header */}
        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-4">
            <div>
              <h2 className="font-display text-2xl text-navy">{eventDetail.event.title}</h2>
              <div className="flex flex-wrap gap-3 mt-2 text-text-mid text-sm">
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {new Date(eventDetail.event.event_date).toLocaleDateString('en-US', {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {eventDetail.event.location}, {eventDetail.event.city}
                </span>
              </div>
              {eventDetail.event.description && (
                <p className="text-text-mid mt-3">{eventDetail.event.description}</p>
              )}
              {eventDetail.event.is_paid && (
                <div className="mt-2">
                  <Badge className="bg-navy text-white text-sm">
                    ${Number(eventDetail.event.price).toFixed(2)} per person
                  </Badge>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setQrOpen(true)}
              className="rounded-full border-navy text-navy hover:bg-navy hover:text-white"
              data-testid="event-qr-btn"
            >
              <QrCode size={16} className="mr-2" /> Event QR
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/checkin/${selectedEvent}`)}
              className="rounded-full border-teal text-teal hover:bg-teal hover:text-white"
              data-testid="open-checkin-scanner-btn"
            >
              <ScanLine size={16} className="mr-2" /> Check-In Scanner
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExportEventRsvps(selectedEvent, eventDetail.event.title)}
              className="rounded-full border-teal text-teal hover:bg-teal hover:text-white"
            >
              <Download size={16} className="mr-2" /> Export RSVPs
            </Button>
            <Button
              variant="outline"
              onClick={() => setGuestModal({ open: true, eventId: selectedEvent, eventTitle: eventDetail.event.title })}
              className="rounded-full border-navy text-navy hover:bg-navy hover:text-white"
              data-testid="register-guest-btn"
            >
              <UserCheck size={16} className="mr-2" /> Register Guest
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDeleteEvent(selectedEvent, eventDetail.event.title)}
              className="rounded-full border-red-400 text-red-600 hover:bg-red-50"
              data-testid="delete-event-detail-btn"
            >
              <XCircle size={16} className="mr-2" /> Delete
            </Button>
          </div>

          {/* Capacity Stats */}
          <div className={`grid ${eventDetail.event.is_paid ? 'grid-cols-4' : 'grid-cols-3'} gap-4 p-4 bg-cream/50 rounded-xl`}>
            <div className="text-center">
              <p className="text-2xl font-bold text-teal">{eventDetail.rsvp_count}</p>
              <p className="text-sm text-text-mid">Confirmed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-navy">{eventDetail.max_capacity}</p>
              <p className="text-sm text-text-mid">Capacity</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold ${eventDetail.spots_remaining > 0 ? 'text-green-600' : 'text-orange-500'}`}>
                {eventDetail.spots_remaining}
              </p>
              <p className="text-sm text-text-mid">Spots Left</p>
            </div>
            {eventDetail.event.is_paid && (
              <div className="text-center">
                <p className="text-2xl font-bold text-green-700">
                  ${((eventDetail.payments || []).filter((p) => p.status === 'paid').reduce((s, p) => s + p.amount_cents, 0) / 100).toFixed(2)}
                </p>
                <p className="text-sm text-text-mid">Revenue</p>
              </div>
            )}
          </div>
        </div>

        {/* Registrations, attendance + financial capture (Phase 3) */}
        <EventOpsPanel
          eventId={selectedEvent}
          isWorkshop={(eventDetail.event.event_type || 'gathering') === 'workshop'}
        />

        <EventQrDialog
          open={qrOpen}
          onOpenChange={setQrOpen}
          eventId={selectedEvent}
          eventTitle={eventDetail.event.title}
        />

        {/* Confirmed RSVPs Table */}
        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-navy flex items-center gap-2">
              <CheckCircle size={18} className="text-green-600" />
              Confirmed RSVPs ({eventDetail.rsvp_count})
            </h3>
            <Dialog open={addRsvpOpen} onOpenChange={setAddRsvpOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-teal hover:bg-teal-deep rounded-full">
                  <Plus size={16} className="mr-1" /> Add RSVP
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add RSVP Manually</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Select Member</Label>
                    <Select value={addRsvpUserId} onValueChange={setAddRsvpUserId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a member" />
                      </SelectTrigger>
                      <SelectContent>
                        {members.filter((m) =>
                          !eventDetail.confirmed_rsvps.find((r) => r.user_id === m.id) &&
                          !eventDetail.waitlist.find((r) => r.user_id === m.id)
                        ).map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.first_name} {member.last_name} ({member.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleAddRsvp}
                    disabled={!addRsvpUserId}
                    className="w-full bg-teal hover:bg-teal-deep rounded-full"
                  >
                    Add RSVP
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {eventDetail.confirmed_rsvps.length === 0 ? (
            <p className="text-center text-text-mid py-8">No confirmed RSVPs yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>RSVP Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventDetail.confirmed_rsvps.map((rsvp) => (
                  <TableRow key={rsvp.id}>
                    <TableCell className="font-medium">
                      {rsvp.user.first_name} {rsvp.user.last_name}
                    </TableCell>
                    <TableCell>{rsvp.user.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        rsvp.user.membership_tier === 'premium' ? 'bg-purple-100 text-purple-700' :
                        rsvp.user.membership_tier === 'core' ? 'bg-teal/20 text-teal' : ''
                      }>
                        {rsvp.user.membership_tier || 'free'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-text-mid">
                      {new Date(rsvp.rsvp_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveRsvp(rsvp.user_id, `${rsvp.user.first_name} ${rsvp.user.last_name}`)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <XCircle size={16} className="mr-1" /> Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Waitlist */}
        {eventDetail.waitlist.length > 0 && (
          <div className="bg-white rounded-2xl shadow-card p-6">
            <h3 className="font-semibold text-navy flex items-center gap-2 mb-4">
              <Clock size={18} className="text-orange-500" />
              Waitlist ({eventDetail.waitlist_count})
            </h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Waitlisted</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventDetail.waitlist.map((rsvp) => (
                  <TableRow key={rsvp.id}>
                    <TableCell className="font-medium">
                      {rsvp.user.first_name} {rsvp.user.last_name}
                    </TableCell>
                    <TableCell>{rsvp.user.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{rsvp.user.membership_tier || 'free'}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-text-mid">
                      {new Date(rsvp.rsvp_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePromoteFromWaitlist(rsvp.user_id, `${rsvp.user.first_name} ${rsvp.user.last_name}`)}
                          className="text-green-600 border-green-300 hover:bg-green-50"
                        >
                          <UserCheck size={16} className="mr-1" /> Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveRsvp(rsvp.user_id, `${rsvp.user.first_name} ${rsvp.user.last_name}`)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <XCircle size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Ticket Payments Table (Paid Events) */}
        {eventDetail.event.is_paid && (eventDetail.payments || []).length > 0 && (
          <div className="bg-white rounded-2xl shadow-card p-6 mt-6" data-testid="event-payments-section">
            <h3 className="font-semibold text-navy flex items-center gap-2 mb-4">
              <Crown size={18} className="text-navy" />
              Ticket Payments ({eventDetail.payments.length})
            </h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventDetail.payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell className="font-medium">{payment.user_name}</TableCell>
                    <TableCell>{payment.user_email}</TableCell>
                    <TableCell className="font-semibold">${(payment.amount_cents / 100).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        payment.status === 'paid' ? 'bg-green-100 text-green-700' :
                        payment.status === 'refunded' ? 'bg-red-100 text-red-700' :
                        payment.status === 'transferred' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }>
                        {payment.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-text-mid">
                      {new Date(payment.paid_at || payment.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {payment.status === 'paid' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
                            data-testid={`refund-payment-${payment.id}`}
                            onClick={(e) => { e.stopPropagation(); handleRefundPayment(eventDetail.event.id, payment.id, payment.user_name); }}
                          >
                            Refund
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs"
                            data-testid={`credit-payment-${payment.id}`}
                            onClick={(e) => { e.stopPropagation(); handleTransferCredit(eventDetail.event.id, payment.id, payment.user_name); }}
                          >
                            Credit
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    );
  }

  // List view
  const totalRevenue = events.reduce((sum, e) => sum + (e.revenue_cents || 0), 0);
  const totalRsvps = events.reduce((sum, e) => sum + (e.rsvp_count || 0), 0);
  const upcomingCount = events.filter((e) => (e.status || 'Upcoming') !== 'Past').length;
  const paidCount = events.filter((e) => e.is_paid).length;

  const handleExportAllPayments = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiUrl}/api/admin/event-payments/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error('Failed to export payments');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `event_payments_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported event payments CSV');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  return (
    <div data-testid="admin-events-list">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h2 className="font-display text-2xl text-navy">Events</h2>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleExportAllPayments}
            variant="outline"
            className="rounded-full border-green-300 text-green-700 hover:bg-green-50"
            data-testid="export-all-payments-btn"
          >
            <Download size={16} className="mr-2" /> Export Payments CSV
          </Button>
          <Dialog open={createEventOpen} onOpenChange={setCreateEventOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal hover:bg-teal-deep rounded-full">
                <Plus size={18} className="mr-2" /> Create Event
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display text-navy">Create New Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>City</Label>
                  <Select value={newEvent.city} onValueChange={(v) => setNewEvent({ ...newEvent, city: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.filter((c) => c !== 'All Cities').map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={newEvent.event_date}
                    onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  placeholder="e.g., The Coffee Bean, 123 Main St"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Max Capacity</Label>
                <Input
                  type="number"
                  value={newEvent.max_capacity}
                  onChange={(e) => setNewEvent({ ...newEvent, max_capacity: parseInt(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  className="rounded-xl"
                  rows={3}
                />
              </div>
              <div>
                <Label>Event Type</Label>
                <Select value={newEvent.event_type || 'gathering'} onValueChange={(v) => setNewEvent({ ...newEvent, event_type: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="create-event-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gathering">Gathering (free for all members)</SelectItem>
                    <SelectItem value="workshop">Workshop (paid — Thrive credit applies)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newEvent.event_type === 'workshop' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Workshop Base Price ($)</Label>
                      <Input
                        type="number" step="0.01" min="0" placeholder="45.00"
                        value={newEvent.base_price}
                        onChange={(e) => setNewEvent({ ...newEvent, base_price: e.target.value })}
                        className="rounded-xl" data-testid="create-event-base-price"
                      />
                    </div>
                    <div>
                      <Label>Host Name</Label>
                      <Input
                        value={newEvent.host_name}
                        onChange={(e) => setNewEvent({ ...newEvent, host_name: e.target.value })}
                        className="rounded-xl" data-testid="create-event-host-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Registration Opens (general access)</Label>
                    <Input
                      type="datetime-local"
                      value={newEvent.registration_opens_at}
                      onChange={(e) => setNewEvent({ ...newEvent, registration_opens_at: e.target.value })}
                      className="rounded-xl" data-testid="create-event-reg-opens"
                    />
                    <p className="text-xs text-text-mid mt-1">Founding members register 48h early, Thrive 24h early. Leave blank to open immediately.</p>
                  </div>
                </>
              )}
              <div>
                <Label>What to Bring (optional)</Label>
                <Input
                  value={newEvent.what_to_bring}
                  onChange={(e) => setNewEvent({ ...newEvent, what_to_bring: e.target.value })}
                  placeholder="e.g., yoga mat, water bottle"
                  className="rounded-xl" data-testid="create-event-what-to-bring"
                />
              </div>
              {/* Safety & disclosure (Master Spec Phase 1) */}
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                  <input type="checkbox" checked={newEvent.children_welcome}
                    onChange={(e) => setNewEvent({ ...newEvent, children_welcome: e.target.checked })}
                    className="w-4 h-4 accent-teal" data-testid="create-event-children-welcome" />
                  Children welcome
                </label>
                <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                  <input type="checkbox" checked={newEvent.third_party_photography}
                    onChange={(e) => setNewEvent({ ...newEvent, third_party_photography: e.target.checked })}
                    className="w-4 h-4 accent-teal" data-testid="create-event-third-party-photo" />
                  Third-party photography
                </label>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                  <input type="checkbox" checked={newEvent.requires_health_disclaimer}
                    onChange={(e) => setNewEvent({ ...newEvent, requires_health_disclaimer: e.target.checked })}
                    className="w-4 h-4 accent-teal" data-testid="create-event-requires-health" />
                  Requires health disclaimer
                </label>
                {newEvent.requires_health_disclaimer && (
                  <Textarea
                    value={newEvent.health_disclaimer}
                    onChange={(e) => setNewEvent({ ...newEvent, health_disclaimer: e.target.value })}
                    placeholder="Health disclaimer shown on the event page before registration"
                    className="rounded-xl mt-2" rows={2} data-testid="create-event-health-disclaimer"
                  />
                )}
              </div>
              <div>
                <Label>Waiver Template</Label>
                <Select
                  value={newEvent.waiver_template || (newEvent.event_type === 'workshop' ? 'workshop' : 'general')}
                  onValueChange={(v) => setNewEvent({ ...newEvent, waiver_template: v })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="create-event-waiver-template">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General (gathering default)</SelectItem>
                    <SelectItem value="workshop">Workshop (adds health/activity language)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-text-mid mt-1">Waiver text is managed separately — this picks which template applies.</p>
              </div>
              {newEvent.event_type === 'workshop' && (
                <div className="bg-cream/60 rounded-xl p-3 space-y-3">
                  <p className="text-xs font-semibold text-navy uppercase tracking-wide">Practitioner credentials (admin-only, never public)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={newEvent.practitioner_license_type}
                      onChange={(e) => setNewEvent({ ...newEvent, practitioner_license_type: e.target.value })}
                      placeholder="License type (e.g. LMFT, RYT-500)"
                      className="rounded-xl" data-testid="create-event-license-type"
                    />
                    <Input
                      value={newEvent.practitioner_license_number}
                      onChange={(e) => setNewEvent({ ...newEvent, practitioner_license_number: e.target.value })}
                      placeholder="License number"
                      className="rounded-xl" data-testid="create-event-license-number"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                    <input type="checkbox" checked={newEvent.practitioner_insurance_verified}
                      onChange={(e) => setNewEvent({ ...newEvent, practitioner_insurance_verified: e.target.checked })}
                      className="w-4 h-4 accent-teal" data-testid="create-event-insurance-verified" />
                    Insurance verified
                  </label>
                </div>
              )}
              <div>
                <Label>Ticket Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00 = Free event"
                  value={newEvent.price}
                  onChange={(e) => setNewEvent({ ...newEvent, price: e.target.value })}
                  className="rounded-xl"
                  data-testid="create-event-price"
                />
                <p className="text-xs text-text-mid mt-1">Leave at 0 or empty for a free event</p>
              </div>
              {newEvent.price > 0 && (
                <div>
                  <Label>Member Price — Connect / Thrive members (optional)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={`Below $${Number(newEvent.price || 0).toFixed(2)} for a discount`}
                    value={newEvent.member_discount_price || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, member_discount_price: e.target.value })}
                    className="rounded-xl"
                    data-testid="create-event-member-price"
                  />
                  <p className="text-xs text-text-mid mt-1">Leave blank if members pay regular price.</p>
                </div>
              )}
              <div>
                <Label>Event Photos (optional)</Label>
                <EventPhotoUploader
                  value={newEvent.photos || (newEvent.photo_url ? [newEvent.photo_url] : [])}
                  onChange={(arr) => setNewEvent({
                    ...newEvent,
                    photos: arr,
                    photo_url: arr[0] || '',
                  })}
                  testIdPrefix="create-event-photos"
                />
              </div>
              <Button
                onClick={handleCreateEvent}
                disabled={!newEvent.title || !newEvent.city || !newEvent.event_date || !newEvent.location}
                className="w-full bg-teal hover:bg-teal-deep rounded-full"
              >
                Create Event
              </Button>
            </div>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Event metrics tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="event-metrics-tiles">
        <div className="bg-white rounded-2xl shadow-card p-4">
          <p className="text-xs text-text-mid uppercase tracking-wide">Total Events</p>
          <p className="text-2xl font-bold text-navy mt-1" data-testid="metric-total-events">{events.length}</p>
          <p className="text-[11px] text-text-mid mt-1">{upcomingCount} upcoming</p>
        </div>
        <div className="bg-white rounded-2xl shadow-card p-4">
          <p className="text-xs text-text-mid uppercase tracking-wide">Total RSVPs</p>
          <p className="text-2xl font-bold text-teal mt-1" data-testid="metric-total-rsvps">{totalRsvps}</p>
          <p className="text-[11px] text-text-mid mt-1">across all events</p>
        </div>
        <div className="bg-white rounded-2xl shadow-card p-4">
          <p className="text-xs text-text-mid uppercase tracking-wide">Paid Events</p>
          <p className="text-2xl font-bold text-navy mt-1" data-testid="metric-paid-events">{paidCount}</p>
          <p className="text-[11px] text-text-mid mt-1">{events.length - paidCount} free</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl shadow-card p-4 border border-green-200">
          <p className="text-xs text-green-700 uppercase tracking-wide font-semibold">Total Revenue</p>
          <p className="text-2xl font-bold text-green-700 mt-1" data-testid="metric-total-revenue">
            ${(totalRevenue / 100).toFixed(2)}
          </p>
          <p className="text-[11px] text-text-mid mt-1">paid tickets</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Capacity</TableHead>
              <TableHead>RSVPs</TableHead>
              <TableHead>Revenue</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-text-mid">
                  No events created yet
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow
                  key={event.id}
                  onClick={() => fetchEventDetail(event.id)}
                  className="cursor-pointer hover:bg-cream/50 transition-colors"
                  data-testid={`event-row-${event.id}`}
                >
                  <TableCell className="font-medium text-navy">{event.title}</TableCell>
                  <TableCell>{event.city}</TableCell>
                  <TableCell>
                    {new Date(event.event_date).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  </TableCell>
                  <TableCell>
                    {event.is_paid ? (
                      <span className="font-semibold text-navy">${Number(event.price).toFixed(2)}</span>
                    ) : (
                      <span className="text-text-mid">Free</span>
                    )}
                  </TableCell>
                  <TableCell>{event.max_capacity}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-teal/10 text-teal">
                      {event.rsvp_count || 0}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {event.is_paid ? (
                      <span className="font-semibold text-green-700">
                        ${((event.revenue_cents || 0) / 100).toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-text-mid">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getEventStatusBadge(event.status || 'Upcoming')}>
                      {event.status || 'Upcoming'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-teal hover:bg-teal/10 text-xs"
                      data-testid={`edit-event-${event.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const dateVal = event.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '';
                        setEditingEvent({
                          id: event.id,
                          title: event.title || '',
                          city: event.city || '',
                          event_date: dateVal,
                          location: event.location || '',
                          max_capacity: event.max_capacity || 50,
                          description: event.description || '',
                          price: event.price || 0,
                          member_discount_price: event.member_discount_price ?? '',
                          photo_url: event.photo_url || '',
                          photos: event.photos || (event.photo_url ? [event.photo_url] : []),
                          event_type: event.event_type || 'gathering',
                          base_price: event.base_price ?? '',
                          host_name: event.host_name || '',
                          what_to_bring: event.what_to_bring || '',
                          registration_opens_at: event.registration_opens_at ? new Date(event.registration_opens_at).toISOString().slice(0, 16) : '',
                          waiver_template: event.waiver_template || (event.event_type === 'workshop' ? 'workshop' : 'general'),
                          children_welcome: !!event.children_welcome,
                          requires_health_disclaimer: !!event.requires_health_disclaimer,
                          health_disclaimer: event.health_disclaimer || '',
                          third_party_photography: !!event.third_party_photography,
                          practitioner_license_type: event.practitioner_license_type || '',
                          practitioner_license_number: event.practitioner_license_number || '',
                          practitioner_insurance_verified: !!event.practitioner_insurance_verified,
                        });
                        setEditEventOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
